/**
 * Vendor Communication Center
 * 
 * Unified messaging hub for vendor communications:
 * - SMS/Email templates
 * - Broadcast messages to vendor groups
 * - Communication history per vendor
 * - Automated reminders
 */

import { useState, useMemo } from 'react';
import {
    MessageSquare,
    Send,
    Mail,
    Phone,
    Clock,
    CheckCircle,
    CheckCheck,
    AlertCircle,
    Users,
    Search,
    Plus,
    FileText,
    Bell,
    Star,
    Loader,
    Edit3,
    Copy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

interface VendorCommunicationCenterProps {
    user: {
        id: string;
        name: string;
        email: string;
    };
    company: {
        id: string;
        name: string;
    };
}

interface MessageTemplate {
    id: string;
    name: string;
    category: 'invitation' | 'reminder' | 'notification' | 'announcement';
    channel: 'sms' | 'email' | 'both';
    subject?: string;
    body: string;
    variables: string[];
    isDefault: boolean;
}

interface CommunicationLog {
    id: string;
    vendorId: string;
    vendorName: string;
    recipientPhone?: string;
    recipientEmail?: string;
    channel: 'sms' | 'email';
    subject?: string;
    body: string;
    status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
    sentAt: Date;
}

const defaultTemplates: MessageTemplate[] = [
    {
        id: 'inv-initial',
        name: 'Vendor Invitation',
        category: 'invitation',
        channel: 'sms',
        body: '{companyName} has invited you to become a vendor on OilWellX. Reply YES to accept this invitation and get started. Reply NO to decline.',
        variables: ['companyName'],
        isDefault: true,
    },
    {
        id: 'inv-magic-link',
        name: 'Magic Link',
        category: 'invitation',
        channel: 'sms',
        body: 'Great! Complete your vendor profile here: {magicLinkUrl}\n\nThis link expires in 48 hours. Need help? Reply HELP',
        variables: ['magicLinkUrl'],
        isDefault: true,
    },
    {
        id: 'rem-insurance',
        name: 'Insurance Expiring',
        category: 'reminder',
        channel: 'sms',
        body: 'Hi {contactName}, your {documentType} expires in {daysRemaining} days. Please upload a new certificate to maintain your active vendor status.',
        variables: ['contactName', 'documentType', 'daysRemaining'],
        isDefault: true,
    },
    {
        id: 'rem-license',
        name: 'License Expiring',
        category: 'reminder',
        channel: 'email',
        subject: 'Action Required: License Expiring Soon',
        body: 'Dear {contactName},\n\nThis is a reminder that your {licenseType} license (#{licenseNumber}) expires on {expirationDate}.\n\nPlease upload a renewed license to the vendor portal to maintain your active status.\n\nThank you,\n{companyName}',
        variables: ['contactName', 'licenseType', 'licenseNumber', 'expirationDate', 'companyName'],
        isDefault: true,
    },
    {
        id: 'not-approved',
        name: 'Vendor Approved',
        category: 'notification',
        channel: 'sms',
        body: '🎉 Congratulations! Your vendor profile has been approved by {companyName}. You\'ll start receiving service requests soon.',
        variables: ['companyName'],
        isDefault: true,
    },
    {
        id: 'not-rejected',
        name: 'Application Declined',
        category: 'notification',
        channel: 'sms',
        body: 'Your vendor application has been declined. Reason: {reason}. Questions? Contact {supportPhone}',
        variables: ['reason', 'supportPhone'],
        isDefault: true,
    },
    {
        id: 'not-ticket',
        name: 'New Service Request',
        category: 'notification',
        channel: 'sms',
        body: '📋 New service request: {ticketTitle} at {location}. View details: {ticketUrl}',
        variables: ['ticketTitle', 'location', 'ticketUrl'],
        isDefault: true,
    },
    {
        id: 'ann-general',
        name: 'General Announcement',
        category: 'announcement',
        channel: 'both',
        subject: 'Important Update from {companyName}',
        body: '{message}',
        variables: ['companyName', 'message'],
        isDefault: true,
    },
];

type TabType = 'compose' | 'templates' | 'history' | 'scheduled';

export default function VendorCommunicationCenter({
    company,
}: VendorCommunicationCenterProps) {
    const [activeTab, setActiveTab] = useState<TabType>('compose');
    const [templates] = useState<MessageTemplate[]>(defaultTemplates);
    const [communicationHistory] = useState<CommunicationLog[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');

    // Compose state
    const [composeChannel, setComposeChannel] = useState<'sms' | 'email'>('sms');
    const [composeRecipients, setComposeRecipients] = useState<string>('');
    const [composeSubject, setComposeSubject] = useState('');
    const [composeBody, setComposeBody] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [isSending, setIsSending] = useState(false);

    const filteredTemplates = useMemo(() => {
        let filtered = templates;

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(t => t.category === selectedCategory);
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(t =>
                t.name.toLowerCase().includes(query) ||
                t.body.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [templates, selectedCategory, searchQuery]);

    const handleSelectTemplate = (templateId: string) => {
        const template = templates.find(t => t.id === templateId);
        if (template) {
            setSelectedTemplate(templateId);
            setComposeBody(template.body);
            if (template.subject) {
                setComposeSubject(template.subject);
            }
            setComposeChannel(template.channel === 'both' ? 'sms' : template.channel);
        }
    };

    const handleSendMessage = async () => {
        if (!composeRecipients.trim() || !composeBody.trim()) {
            toast.error('Please fill in all required fields');
            return;
        }

        setIsSending(true);
        try {
            await new Promise(resolve => setTimeout(resolve, 1500));
            toast.success(`Message sent to ${composeRecipients.split(',').length} recipient(s)`);

            setComposeRecipients('');
            setComposeSubject('');
            setComposeBody('');
            setSelectedTemplate('');
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setIsSending(false);
        }
    };

    const handleCopyTemplate = (template: MessageTemplate) => {
        navigator.clipboard.writeText(template.body);
        toast.success('Template copied to clipboard');
    };

    const getChannelIcon = (channel: string) => {
        switch (channel) {
            case 'sms': return <Phone className="h-4 w-4" />;
            case 'email': return <Mail className="h-4 w-4" />;
            case 'both': return <MessageSquare className="h-4 w-4" />;
            default: return null;
        }
    };

    const getCategoryColor = (category: string) => {
        switch (category) {
            case 'invitation': return 'bg-blue-100 text-blue-700';
            case 'reminder': return 'bg-amber-100 text-amber-700';
            case 'notification': return 'bg-green-100 text-green-700';
            case 'announcement': return 'bg-purple-100 text-purple-700';
            default: return 'bg-gray-100 text-gray-700';
        }
    };

    const getStatusBadge = (status: CommunicationLog['status']) => {
        switch (status) {
            case 'pending': return <Badge className="bg-gray-100 text-gray-700"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
            case 'sent': return <Badge className="bg-blue-100 text-blue-700"><CheckCircle className="h-3 w-3 mr-1" />Sent</Badge>;
            case 'delivered': return <Badge className="bg-green-100 text-green-700"><CheckCheck className="h-3 w-3 mr-1" />Delivered</Badge>;
            case 'read': return <Badge className="bg-purple-100 text-purple-700"><CheckCheck className="h-3 w-3 mr-1" />Read</Badge>;
            case 'failed': return <Badge className="bg-red-100 text-red-700"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>;
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center space-x-3">
                        <MessageSquare className="h-8 w-8 text-blue-600" />
                        <span>Communication Center</span>
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Send messages and manage vendor communications
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-2 border-b border-gray-200">
                {[
                    { id: 'compose', label: 'Compose', icon: Send },
                    { id: 'templates', label: 'Templates', icon: FileText },
                    { id: 'history', label: 'History', icon: Clock },
                    { id: 'scheduled', label: 'Scheduled', icon: Bell },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as TabType)}
                        className={`flex items-center space-x-2 px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === tab.id
                                ? 'text-blue-600 border-blue-600'
                                : 'text-gray-600 border-transparent hover:text-gray-900'
                            }`}
                    >
                        <tab.icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'compose' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Compose Form */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">New Message</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Channel Selection */}
                                <div className="flex space-x-2">
                                    <Button
                                        variant={composeChannel === 'sms' ? 'default' : 'outline'}
                                        onClick={() => setComposeChannel('sms')}
                                    >
                                        <Phone className="h-4 w-4 mr-2" />
                                        SMS
                                    </Button>
                                    <Button
                                        variant={composeChannel === 'email' ? 'default' : 'outline'}
                                        onClick={() => setComposeChannel('email')}
                                    >
                                        <Mail className="h-4 w-4 mr-2" />
                                        Email
                                    </Button>
                                </div>

                                {/* Recipients */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Recipients
                                    </label>
                                    <div className="relative">
                                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <Input
                                            placeholder={composeChannel === 'sms' ? 'Enter phone numbers (comma separated)' : 'Enter email addresses (comma separated)'}
                                            value={composeRecipients}
                                            onChange={(e) => setComposeRecipients(e.target.value)}
                                            className="pl-10"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Or select vendors from the directory
                                    </p>
                                </div>

                                {/* Template Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Template (Optional)
                                    </label>
                                    <select
                                        value={selectedTemplate}
                                        onChange={(e) => handleSelectTemplate(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white"
                                    >
                                        <option value="">-- Select a template --</option>
                                        {templates.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Subject (Email only) */}
                                {composeChannel === 'email' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Subject
                                        </label>
                                        <Input
                                            placeholder="Enter subject line"
                                            value={composeSubject}
                                            onChange={(e) => setComposeSubject(e.target.value)}
                                        />
                                    </div>
                                )}

                                {/* Message Body */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Message
                                    </label>
                                    <textarea
                                        placeholder="Type your message..."
                                        value={composeBody}
                                        onChange={(e) => setComposeBody(e.target.value)}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none min-h-[150px]"
                                        rows={6}
                                    />
                                    {composeChannel === 'sms' && (
                                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                                            <span>{composeBody.length} characters</span>
                                            <span>{Math.ceil(composeBody.length / 160)} SMS segment(s)</span>
                                        </div>
                                    )}
                                </div>

                                {/* Send Button */}
                                <div className="flex justify-end space-x-3">
                                    <Button variant="outline" onClick={() => {
                                        setComposeRecipients('');
                                        setComposeSubject('');
                                        setComposeBody('');
                                        setSelectedTemplate('');
                                    }}>
                                        Clear
                                    </Button>
                                    <Button
                                        onClick={handleSendMessage}
                                        disabled={isSending || !composeRecipients || !composeBody}
                                        className="bg-blue-600 hover:bg-blue-700"
                                    >
                                        {isSending ? (
                                            <>
                                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="h-4 w-4 mr-2" />
                                                Send Message
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Quick Actions & Stats */}
                    <div className="space-y-6">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Quick Actions</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <Button variant="outline" className="w-full justify-start">
                                    <Users className="h-4 w-4 mr-2" />
                                    Message All Active Vendors
                                </Button>
                                <Button variant="outline" className="w-full justify-start">
                                    <Bell className="h-4 w-4 mr-2" />
                                    Send Compliance Reminders
                                </Button>
                                <Button variant="outline" className="w-full justify-start">
                                    <Star className="h-4 w-4 mr-2" />
                                    Send Performance Reports
                                </Button>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Delivery Stats</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center p-3 bg-green-50 rounded-lg">
                                        <p className="text-2xl font-bold text-green-600">98%</p>
                                        <p className="text-xs text-green-700">Delivery Rate</p>
                                    </div>
                                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                                        <p className="text-2xl font-bold text-blue-600">45%</p>
                                        <p className="text-xs text-blue-700">Open Rate</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {activeTab === 'templates' && (
                <div className="space-y-6">
                    {/* Filters */}
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                                placeholder="Search templates..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <div className="flex gap-2">
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg bg-white"
                            >
                                <option value="all">All Categories</option>
                                <option value="invitation">Invitation</option>
                                <option value="reminder">Reminder</option>
                                <option value="notification">Notification</option>
                                <option value="announcement">Announcement</option>
                            </select>
                            <Button>
                                <Plus className="h-4 w-4 mr-2" />
                                New Template
                            </Button>
                        </div>
                    </div>

                    {/* Template Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredTemplates.map(template => (
                            <Card key={template.id} className="hover:shadow-md transition-shadow">
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="flex items-center space-x-2">
                                            {getChannelIcon(template.channel)}
                                            <h3 className="font-semibold text-gray-900">{template.name}</h3>
                                        </div>
                                        <Badge className={getCategoryColor(template.category)}>
                                            {template.category}
                                        </Badge>
                                    </div>

                                    {template.subject && (
                                        <p className="text-sm font-medium text-gray-700 mb-2">
                                            Subject: {template.subject}
                                        </p>
                                    )}

                                    <p className="text-sm text-gray-600 line-clamp-3 mb-3">
                                        {template.body}
                                    </p>

                                    {template.variables.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-3">
                                            {template.variables.map(v => (
                                                <Badge key={v} variant="outline" className="text-xs">
                                                    {`{${v}}`}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex items-center justify-between pt-2 border-t">
                                        <div className="flex items-center space-x-2">
                                            {template.isDefault && (
                                                <Badge variant="outline" className="text-xs">Default</Badge>
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <Button variant="ghost" size="sm" onClick={() => handleCopyTemplate(template)}>
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm">
                                                <Edit3 className="h-4 w-4" />
                                            </Button>
                                            <Button variant="ghost" size="sm" onClick={() => handleSelectTemplate(template.id)}>
                                                <Send className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>

                    {filteredTemplates.length === 0 && (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates found</h3>
                                <p className="text-gray-600">Try adjusting your filters or create a new template</p>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {activeTab === 'history' && (
                <div className="space-y-4">
                    {communicationHistory.length === 0 ? (
                        <Card>
                            <CardContent className="py-12 text-center">
                                <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">No communication history</h3>
                                <p className="text-gray-600">Messages you send will appear here</p>
                            </CardContent>
                        </Card>
                    ) : (
                        communicationHistory.map(log => (
                            <Card key={log.id}>
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start space-x-3">
                                            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${log.channel === 'sms' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                                                }`}>
                                                {log.channel === 'sms' ? <Phone className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900">{log.vendorName}</p>
                                                <p className="text-sm text-gray-600">
                                                    {log.channel === 'sms' ? log.recipientPhone : log.recipientEmail}
                                                </p>
                                                {log.subject && (
                                                    <p className="text-sm font-medium text-gray-700 mt-1">{log.subject}</p>
                                                )}
                                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">{log.body}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            {getStatusBadge(log.status)}
                                            <p className="text-xs text-gray-500 mt-2">
                                                {formatDistanceToNow(log.sentAt, { addSuffix: true })}
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            )}

            {activeTab === 'scheduled' && (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No scheduled messages</h3>
                        <p className="text-gray-600 mb-4">Schedule messages to be sent later</p>
                        <Button onClick={() => setActiveTab('compose')}>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Scheduled Message
                        </Button>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
